import { useCallback, useLayoutEffect, useRef } from "react";

// ENG-3502 — where keyboard focus goes after an action takes its own control away.
//
// Deactivating a member hides their ⋮ menu, revoking an invitation or an exception removes its row,
// and pressing Undo closes the snackbar that held focus. Each time the focused control left the page,
// and focus fell to <body>: the next Tab started again from the top of the page. So the control is
// remembered before the action runs, along with the tab order around it, and focus goes back to it if
// it is still there, or else to the next control after it (the one before it, at the end of a list).

const FOCUSABLE = "a[href], button, input, select, textarea, [tabindex]";

/** Can take focus from Tab. Visibility is not checked: jsdom has no layout, and hidden rows unmount. */
const tabbable = (el: HTMLElement): boolean =>
  el.isConnected
  && !el.hasAttribute("disabled")
  && el.getAttribute("tabindex") !== "-1"
  && el.getAttribute("aria-disabled") !== "true"
  && !el.closest("[hidden], [inert]");

/**
 * Remember where focus should go back to once [from]'s action is done. Call it BEFORE the action, while
 * [from] and its row are still on the page; call what it returns afterwards.
 *
 * The returned function leaves focus alone if it has already landed somewhere in [from]'s part of the
 * page (a dialog's own restore found the control, or the admin moved on). It only steps in when focus
 * is on <body>, still on something outside that part, such as a closing dialog, or inside [leaving]:
 * the snackbar whose Undo was just pressed, which sits in the page and is on its way out.
 */
export type FocusReturn = (leaving?: Element | null) => void;

export function rememberFocusReturn(from: Element | null | undefined): FocusReturn {
  if (!(from instanceof HTMLElement) || from === from.ownerDocument.body) return () => {};
  const doc = from.ownerDocument;
  // The part of the page the control lives in: its dialog when it is in one, else the main content.
  const scope: HTMLElement = from.closest<HTMLElement>("[role='dialog']") ?? from.closest<HTMLElement>("main") ?? doc.body;
  // Not filtered yet: while a dialog is open, the page behind it is aria-hidden, and that is fine.
  const order = Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE));
  const at = order.indexOf(from);

  return (leaving) => {
    const active = doc.activeElement;
    const settled = active instanceof HTMLElement && active !== doc.body && active.isConnected
      && scope.contains(active) && !leaving?.contains(active);
    if (settled) return;
    const candidates = at < 0 ? [from] : [from, ...order.slice(at + 1), ...order.slice(0, at).reverse()];
    const target = candidates.find((el) => tabbable(el) && !leaving?.contains(el));
    // Nothing left to land on: the part of the page itself, when it can hold focus (the main content can).
    if (target) target.focus();
    else if (scope !== doc.body && scope.hasAttribute("tabindex")) scope.focus();
  };
}

/**
 * When [returnFocusSoon] tries. A dialog restores focus in its own cleanup, at once; but the list that
 * drops a removed row re-renders when its refetch lands, which can be a moment after the action
 * resolves. A control focused before that commit leaves the page with it, so later tries pick up
 * after it. Each try does nothing once focus has landed in the page, so they cannot fight the admin.
 */
const RETURN_FOCUS_TRIES_MS = [0, 100, 300];

/** [rememberFocusReturn]'s function, run once the current render has settled, and again after. */
export const returnFocusSoon = (restore: FocusReturn, leaving?: Element | null): void => {
  for (const ms of RETURN_FOCUS_TRIES_MS) setTimeout(() => restore(leaving), ms);
};

/**
 * ENG-3502 — a dialog's half of the above, shared by ConfirmationDialog and RevokeExceptionDialog.
 * Remembers where focus was as the dialog OPENS (only then: once open, the focused control is the
 * dialog's own), in a layout effect so it reads focus before the dialog's own effect moves it. Returns
 * the function to call when the dialog closes, whichever way; it runs once per opening.
 *
 * [from] names the control to come back to when focus is not on it as the dialog opens (a menu item's
 * menu has closed). Without it, the focused control is taken if it is in the page — in the main
 * content or a dialog — and nothing otherwise, since a control in a menu's popup is gone by then.
 */
export function useDialogFocusReturn(open: boolean, from?: HTMLElement | null): () => void {
  const pending = useRef<FocusReturn | null>(null);
  const wasOpen = useRef(false);
  // Read at the opening, whichever render passed it.
  const fromRef = useRef(from);
  fromRef.current = from;
  useLayoutEffect(() => {
    const opening = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opening) return;
    const focused = document.activeElement;
    const inPage = focused instanceof HTMLElement && focused.closest("main, [role='dialog']") ? focused : null;
    pending.current = rememberFocusReturn(fromRef.current ?? inPage);
  }, [open]);
  return useCallback(() => {
    if (pending.current) returnFocusSoon(pending.current);
    pending.current = null;
  }, []);
}
